import React, { useState, useEffect } from "react";
import {
  Rocket,
  Users,
  Building,
  ShoppingCart,
  UserPlus,
  Check,
  ChevronRight,
  ChevronLeft,
  AlertTriangle,
  ExternalLink,
} from "lucide-react";
import {
  clientsAuthApi,
  facilitiesApi,
  productsApi,
  ordersApi,
  authFetch,
} from "../../utils/api";
import { CLIENT_PORTAL_URL, SERVICE_TYPES } from "../../constants";

const VERTICALS = [
  "healthcare",
  "legal",
  "finance",
  "manufacturing",
  "retail",
  "construction",
  "political",
  "informational",
];

const FACILITY_ID_RE = /^[A-Z0-9_]{2,20}$/;

const slugify = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// products.included_services may be a JS array or a Postgres "{a,b}" literal
const productServices = (p) => {
  const raw = p?.included_services ?? p?.includedServices;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.startsWith("{"))
    return raw.slice(1, -1).split(",").filter(Boolean);
  const legacy = [];
  if (p?.includes_rag || p?.includesRag) legacy.push("rag");
  if (p?.includes_slm || p?.includesSlm) legacy.push("slm");
  if (p?.includes_private_llm || p?.includesPrivateLlm)
    legacy.push("private_llm");
  return legacy;
};

const extractId = (res, key) => {
  const row = Array.isArray(res) ? res[0] : res;
  return row?.[key] ?? row?.id ?? null;
};

const STEPS = [
  { id: 1, label: "Client", icon: Users },
  { id: 2, label: "Facility", icon: Building },
  { id: 3, label: "Product & Order", icon: ShoppingCart },
  { id: 4, label: "Invite Admin", icon: UserPlus },
  { id: 5, label: "Done", icon: Check },
];

const inputCls =
  "w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500";
const labelCls = "block text-sm font-medium text-slate-700 mb-1";

const Field = ({ label, required, children, hint }) => (
  <div>
    <label className={labelCls}>
      {label} {required && "*"}
    </label>
    {children}
    {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
  </div>
);

const ErrorBanner = ({ error }) =>
  error ? (
    <div className="flex items-start space-x-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <span className="break-all">{error}</span>
    </div>
  ) : null;

const ClientOnboardingWizard = ({ onNavigate }) => {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Step 1 — client
  const [clientMode, setClientMode] = useState("new"); // "new" | "existing"
  const [existingClients, setExistingClients] = useState([]);
  const [clientForm, setClientForm] = useState({
    name: "",
    slug: "",
    description: "",
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState("");

  // Step 2 — facility
  const [facilityForm, setFacilityForm] = useState({
    facility_id: "",
    facility_name: "",
    contact_email: "",
    vertical_id: "construction",
    timezone: "America/New_York",
  });

  // Step 3 — product & order
  const [products, setProducts] = useState([]);
  const [orderForm, setOrderForm] = useState({
    product_code: "",
    monthly_price: "",
    notes: "",
    provision: true,
    activate: true,
  });

  // Step 4 — invite
  const [inviteForm, setInviteForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
  });

  // Accumulated results
  const [result, setResult] = useState({
    clientId: null,
    clientName: "",
    facilityId: null,
    orderId: null,
    services: [],
    invitedEmail: null,
  });

  useEffect(() => {
    clientsAuthApi
      .getAll()
      .then(setExistingClients)
      .catch(() => {});
    productsApi
      .getAll()
      .then((data) =>
        setProducts(
          (Array.isArray(data) ? data : []).filter(
            (p) => (p.is_active ?? p.isActive) !== false,
          ),
        ),
      )
      .catch(() => {});
  }, []);

  const selectedProduct = products.find(
    (p) => (p.product_code || p.productCode) === orderForm.product_code,
  );

  // ── Step submit handlers ─────────────────────────────────────────

  const submitClient = async () => {
    setError(null);
    if (clientMode === "existing") {
      const c = existingClients.find((c) => c.id === selectedClientId);
      if (!c) return setError("Select a client.");
      setResult((r) => ({ ...r, clientId: c.id, clientName: c.name }));
      setStep(2);
      return;
    }
    if (!clientForm.name.trim()) return setError("Client name is required.");
    setBusy(true);
    try {
      const res = await clientsAuthApi.create({
        name: clientForm.name.trim(),
        slug: clientForm.slug.trim() || slugify(clientForm.name),
        description: clientForm.description,
        is_active: true,
      });
      if (res?.error) throw new Error(res.error);
      if (!res?.id) throw new Error("Client created but no id returned.");
      setResult((r) => ({
        ...r,
        clientId: res.id,
        clientName: clientForm.name.trim(),
      }));
      setStep(2);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const submitFacility = async () => {
    setError(null);
    const fid = facilityForm.facility_id.trim().toUpperCase();
    if (!FACILITY_ID_RE.test(fid))
      return setError(
        "Facility ID must be 2-20 characters: A-Z, 0-9, underscore.",
      );
    if (!facilityForm.facility_name.trim())
      return setError("Facility name is required.");
    setBusy(true);
    try {
      const res = await facilitiesApi.create({
        ...facilityForm,
        facility_id: fid,
        status: "active",
      });
      if (res?.error) throw new Error(res.error);
      const link = await clientsAuthApi.linkFacility(fid, result.clientId);
      if (link?.error)
        throw new Error(`Facility created, link failed: ${link.error}`);
      setResult((r) => ({ ...r, facilityId: fid }));
      setStep(3);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const submitOrder = async () => {
    setError(null);
    if (!orderForm.product_code) return setError("Select a product.");
    setBusy(true);
    try {
      const createRes = await ordersApi.create({
        facility_id: result.facilityId,
        product_code: orderForm.product_code,
        monthly_price: parseFloat(orderForm.monthly_price) || 0,
        notes: orderForm.notes,
      });
      let orderId = extractId(createRes, "setorders");
      if (!orderId) {
        // Fallback: find the newest order for this facility+product
        const orders = await ordersApi.getByFacility(result.facilityId);
        const match = (Array.isArray(orders) ? orders : [])
          .filter(
            (o) => (o.product_code || o.productCode) === orderForm.product_code,
          )
          .sort((a, b) => (b.id || 0) - (a.id || 0))[0];
        orderId = match?.id ?? null;
      }
      if (!orderId)
        throw new Error("Order created but id could not be resolved.");

      const services = productServices(selectedProduct);
      if (orderForm.provision) {
        await ordersApi.provision(orderId);
        // Real infrastructure setup (Pinecone namespace, RAG config)
        await authFetch("/orders/provision-infra", {
          method: "POST",
          body: JSON.stringify({ order_id: orderId }),
        }).catch(() => {});
        if (orderForm.activate) await ordersApi.activate(orderId);
      }
      setResult((r) => ({ ...r, orderId, services }));
      setStep(4);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const submitInvite = async () => {
    setError(null);
    const email = inviteForm.email.trim().toLowerCase();
    if (!email) return setError("Email is required (or skip this step).");
    setBusy(true);
    try {
      // One call grants access to every provisioned service (backend
      // defaults to all facility services when service_types is empty).
      const res = await authFetch("/facility-users/invite", {
        method: "POST",
        body: JSON.stringify({
          facility_id: result.facilityId,
          email,
          first_name: inviteForm.first_name,
          last_name: inviteForm.last_name,
          service_types: result.services,
          access_role: "admin",
          // This invite is the facility's first ADMIN, who works in the
          // Client Portal — not the field app, and never this console.
          app_url: CLIENT_PORTAL_URL,
        }),
      });
      if (res?.error) throw new Error(res.error);
      setResult((r) => ({ ...r, invitedEmail: email }));
      setStep(5);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────

  const StepDots = () => (
    <div className="flex items-center space-x-2 mb-6">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const state = s.id < step ? "done" : s.id === step ? "current" : "todo";
        return (
          <React.Fragment key={s.id}>
            {i > 0 && <div className="w-8 h-px bg-slate-200" />}
            <div
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                state === "done"
                  ? "bg-green-100 text-green-700"
                  : state === "current"
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 text-slate-400"
              }`}
            >
              {state === "done" ? (
                <Check className="w-3.5 h-3.5" />
              ) : (
                <Icon className="w-3.5 h-3.5" />
              )}
              <span>{s.label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  const NavButtons = ({ onNext, nextLabel = "Continue", skippable }) => (
    <div className="flex justify-between pt-6">
      <button
        onClick={() => setStep((s) => Math.max(1, s - 1))}
        disabled={busy || step === 1}
        className="px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40 flex items-center"
      >
        <ChevronLeft className="w-4 h-4 mr-1" /> Back
      </button>
      <div className="space-x-3">
        {skippable && (
          <button
            onClick={() => setStep(5)}
            disabled={busy}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50"
          >
            Skip
          </button>
        )}
        <button
          onClick={onNext}
          disabled={busy}
          className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 inline-flex items-center"
        >
          {busy ? "Working..." : nextLabel}
          {!busy && <ChevronRight className="w-4 h-4 ml-1" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl">
      <div className="flex items-center space-x-3 mb-2">
        <Rocket className="w-5 h-5 text-blue-600" />
        <h1 className="text-lg font-semibold text-slate-800">
          New Client Setup
        </h1>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        Client → facility → order → services → admin invite, in one pass.
      </p>

      <StepDots />

      <div className="bg-white rounded-lg shadow-md border border-slate-200 p-6 space-y-4">
        <ErrorBanner error={error} />

        {/* ── Step 1: Client ── */}
        {step === 1 && (
          <>
            <div className="flex space-x-2">
              {[
                { id: "new", label: "New client" },
                { id: "existing", label: "Existing client" },
              ].map((m) => (
                <button
                  key={m.id}
                  onClick={() => setClientMode(m.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                    clientMode === m.id
                      ? "bg-blue-50 border-blue-400 text-blue-700"
                      : "border-slate-200 text-slate-500 hover:bg-slate-50"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {clientMode === "new" ? (
              <div className="space-y-4">
                <Field label="Client Name" required>
                  <input
                    className={inputCls}
                    value={clientForm.name}
                    onChange={(e) =>
                      setClientForm((f) => ({
                        ...f,
                        name: e.target.value,
                        slug: slugTouched ? f.slug : slugify(e.target.value),
                      }))
                    }
                    placeholder="e.g. Acme Builders"
                  />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Slug">
                    <input
                      className={inputCls}
                      value={clientForm.slug}
                      onChange={(e) => {
                        setSlugTouched(true);
                        setClientForm((f) => ({
                          ...f,
                          slug: slugify(e.target.value),
                        }));
                      }}
                    />
                  </Field>
                  <Field label="Description">
                    <input
                      className={inputCls}
                      value={clientForm.description}
                      onChange={(e) =>
                        setClientForm((f) => ({
                          ...f,
                          description: e.target.value,
                        }))
                      }
                    />
                  </Field>
                </div>
              </div>
            ) : (
              <Field label="Client" required>
                <select
                  className={inputCls}
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                >
                  <option value="">— Select client —</option>
                  {existingClients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <NavButtons onNext={submitClient} />
          </>
        )}

        {/* ── Step 2: Facility ── */}
        {step === 2 && (
          <>
            <p className="text-sm text-slate-500">
              Client: <span className="font-medium">{result.clientName}</span>.
              The facility is created, then linked to this client. Branding,
              prompts, and advanced config live in Facilities → Edit afterwards.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Field
                label="Facility ID"
                required
                hint="2-20 chars, A-Z 0-9 _ — permanent identifier"
              >
                <input
                  className={`${inputCls} font-mono`}
                  value={facilityForm.facility_id}
                  onChange={(e) =>
                    setFacilityForm((f) => ({
                      ...f,
                      facility_id: e.target.value.toUpperCase(),
                    }))
                  }
                  placeholder="ACME01"
                />
              </Field>
              <Field label="Facility Name" required>
                <input
                  className={inputCls}
                  value={facilityForm.facility_name}
                  onChange={(e) =>
                    setFacilityForm((f) => ({
                      ...f,
                      facility_name: e.target.value,
                    }))
                  }
                  placeholder="Acme Builders HQ"
                />
              </Field>
              <Field label="Contact Email">
                <input
                  className={inputCls}
                  type="email"
                  value={facilityForm.contact_email}
                  onChange={(e) =>
                    setFacilityForm((f) => ({
                      ...f,
                      contact_email: e.target.value,
                    }))
                  }
                />
              </Field>
              <Field label="Vertical">
                <select
                  className={inputCls}
                  value={facilityForm.vertical_id}
                  onChange={(e) =>
                    setFacilityForm((f) => ({
                      ...f,
                      vertical_id: e.target.value,
                    }))
                  }
                >
                  {VERTICALS.map((v) => (
                    <option key={v} value={v}>
                      {v.charAt(0).toUpperCase() + v.slice(1)}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <NavButtons onNext={submitFacility} />
          </>
        )}

        {/* ── Step 3: Product & Order ── */}
        {step === 3 && (
          <>
            <p className="text-sm text-slate-500">
              Facility{" "}
              <span className="font-mono font-medium">{result.facilityId}</span>{" "}
              — ordering a product provisions its services automatically.
            </p>
            <Field label="Product" required>
              <select
                className={inputCls}
                value={orderForm.product_code}
                onChange={(e) => {
                  const p = products.find(
                    (x) => (x.product_code || x.productCode) === e.target.value,
                  );
                  setOrderForm((f) => ({
                    ...f,
                    product_code: e.target.value,
                    monthly_price:
                      p?.base_price ?? p?.basePrice ?? f.monthly_price,
                  }));
                }}
              >
                <option value="">— Select product —</option>
                {products.map((p) => (
                  <option key={p.id} value={p.product_code || p.productCode}>
                    {(p.product_code || p.productCode) +
                      " — " +
                      (p.product_name || p.productName)}
                  </option>
                ))}
              </select>
            </Field>
            {selectedProduct && (
              <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
                Services provisioned:{" "}
                {productServices(selectedProduct)
                  .map(
                    (s) => SERVICE_TYPES.find((t) => t.value === s)?.label || s,
                  )
                  .join(", ") || "none defined on this product"}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Monthly Price ($)">
                <input
                  className={inputCls}
                  type="number"
                  step="0.01"
                  value={orderForm.monthly_price}
                  onChange={(e) =>
                    setOrderForm((f) => ({
                      ...f,
                      monthly_price: e.target.value,
                    }))
                  }
                />
              </Field>
              <Field label="Notes">
                <input
                  className={inputCls}
                  value={orderForm.notes}
                  onChange={(e) =>
                    setOrderForm((f) => ({ ...f, notes: e.target.value }))
                  }
                />
              </Field>
            </div>
            <div className="flex space-x-6">
              <label className="flex items-center space-x-2 cursor-pointer text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={orderForm.provision}
                  onChange={(e) =>
                    setOrderForm((f) => ({
                      ...f,
                      provision: e.target.checked,
                      activate: e.target.checked ? f.activate : false,
                    }))
                  }
                  className="h-4 w-4 text-blue-600 rounded border-slate-300"
                />
                <span>Provision services now</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={orderForm.activate}
                  disabled={!orderForm.provision}
                  onChange={(e) =>
                    setOrderForm((f) => ({
                      ...f,
                      activate: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 text-blue-600 rounded border-slate-300 disabled:opacity-40"
                />
                <span>Activate immediately</span>
              </label>
            </div>
            <NavButtons onNext={submitOrder} nextLabel="Create Order" />
          </>
        )}

        {/* ── Step 4: Invite admin ── */}
        {step === 4 && (
          <>
            <p className="text-sm text-slate-500">
              Invite the client's admin user — they get an email with portal
              access to{" "}
              {result.services.length
                ? result.services
                    .map(
                      (s) =>
                        SERVICE_TYPES.find((t) => t.value === s)?.label || s,
                    )
                    .join(", ")
                : "the facility"}
              .
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Email" required>
                <input
                  className={inputCls}
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) =>
                    setInviteForm((f) => ({ ...f, email: e.target.value }))
                  }
                />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="First Name">
                  <input
                    className={inputCls}
                    value={inviteForm.first_name}
                    onChange={(e) =>
                      setInviteForm((f) => ({
                        ...f,
                        first_name: e.target.value,
                      }))
                    }
                  />
                </Field>
                <Field label="Last Name">
                  <input
                    className={inputCls}
                    value={inviteForm.last_name}
                    onChange={(e) =>
                      setInviteForm((f) => ({
                        ...f,
                        last_name: e.target.value,
                      }))
                    }
                  />
                </Field>
              </div>
            </div>
            <NavButtons
              onNext={submitInvite}
              nextLabel="Send Invite"
              skippable
            />
          </>
        )}

        {/* ── Step 5: Summary ── */}
        {step === 5 && (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 text-green-700">
              <Check className="w-5 h-5" />
              <span className="font-semibold">Client setup complete</span>
            </div>
            <table className="text-sm w-full">
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="py-2 text-slate-500 w-40">Client</td>
                  <td className="py-2 font-medium text-slate-800">
                    {result.clientName}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 text-slate-500">Facility</td>
                  <td className="py-2 font-mono text-slate-800">
                    {result.facilityId}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 text-slate-500">Order</td>
                  <td className="py-2 text-slate-800">
                    {result.orderId ? `#${result.orderId}` : "—"}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 text-slate-500">Services</td>
                  <td className="py-2 text-slate-800">
                    {result.services
                      .map(
                        (s) =>
                          SERVICE_TYPES.find((t) => t.value === s)?.label || s,
                      )
                      .join(", ") || "—"}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 text-slate-500">Admin invited</td>
                  <td className="py-2 text-slate-800">
                    {result.invitedEmail || "skipped"}
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="flex space-x-3 pt-2">
              {onNavigate && (
                <>
                  <button
                    onClick={() => onNavigate("facilities")}
                    className="px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50 inline-flex items-center"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                    Configure facility
                  </button>
                  <button
                    onClick={() => onNavigate("orders")}
                    className="px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50 inline-flex items-center"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                    View order
                  </button>
                </>
              )}
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
              >
                Set up another client
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientOnboardingWizard;
